import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Baby, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MainLogin({ onLoginSuccess }) {
  const [formData, setFormData] = useState({
    identifier: '',
    date_of_birth: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginEnabled, setLoginEnabled] = useState(true);
  const [joinSyncEnabled, setJoinSyncEnabled] = useState(true);
  const [checkingSettings, setCheckingSettings] = useState(true);
  const navigate = useNavigate();

  // Check if login and join & sync are enabled
  React.useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await axios.get(`${API}/baby-app-settings`);
        setLoginEnabled(response.data.login_enabled);
        setJoinSyncEnabled(response.data.join_sync_enabled !== false); // Default to true if not set
      } catch (error) {
        console.error('Error checking login status:', error);
        // Default to enabled if error
        setLoginEnabled(true);
        setJoinSyncEnabled(true);
      } finally {
        setCheckingSettings(false);
      }
    };
    checkLoginStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/babies/login`, formData);
      
      // Call parent callback with baby data
      onLoginSuccess(response.data.baby);
      toast.success('Login successful!');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Invalid ID or birthday');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    
    // Auto-uppercase all characters for identifier (baby name/ID)
    if (name === 'identifier') {
      value = value.toUpperCase();
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle date input with auto-advance
  const handleDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData({
      ...formData,
      date_of_birth: value
    });
  };

  // Handle backspace for natural deletion
  const handleDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setFormData({
          ...formData,
          date_of_birth: newValue
        });
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  // Show loading while checking settings
  if (checkingSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-pink-50 p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show message if login is disabled
  if (!loginEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-pink-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                <Baby className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl">My Health Record</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-4">
                <p className="text-orange-800 font-medium mb-2">Login Currently Disabled</p>
                <p className="text-sm text-orange-600">
                  Baby app login is currently disabled by the administrator. Please contact support for assistance.
                </p>
              </div>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="mt-4"
              >
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-pink-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
              <Baby className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">My Health Record</CardTitle>
          <CardDescription>Login to access your health dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier">ID</Label>
              <Input
                id="identifier"
                name="identifier"
                type="text"
                placeholder="Enter ID"
                value={formData.identifier}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date_of_birth">
                Birthday
                <Calendar className="inline w-4 h-4 ml-2 text-gray-500" />
              </Label>
              <Input
                id="date_of_birth"
                name="date_of_birth"
                type="text"
                placeholder="DD/MM/YYYY"
                value={formData.date_of_birth}
                onChange={handleDateInput}
                onKeyDown={handleDateKeyDown}
                required
                maxLength={10}
              />
            </div>

            {error && (
              <div className="text-sm p-3 rounded bg-red-50 text-red-700">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </Button>

            <div className="text-center pt-2 border-t">
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline"
                onClick={() => navigate('/setup')}
              >
                Don't have an account? Create Profile
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                className="text-sm text-purple-600 hover:underline"
                onClick={() => navigate('/dependent-login')}
              >
                Guest Access
              </button>
            </div>

            {joinSyncEnabled && (
              <div className="text-center pt-2 border-t">
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:underline font-medium"
                  onClick={() => navigate('/join-sync')}
                >
                  Join & Sync
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Sync your data across devices
                </p>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default MainLogin;
